import {
  Component,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ElementRef,
  AfterViewInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FencePreviewItem } from '../models/fence-preview-item';
import { FencePreviewItemTypeEnum } from '../models/fence-preview-item-type.enum';

@Component({
  selector: 'ng-fence-preview-element',
  templateUrl: './preview-element.component.html',
})
export class FencePreviewElementComponent implements AfterViewInit {
  @Input() item: FencePreviewItem;
  @Output() isLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChildren('boardImageElement') boardImages?: QueryList<ElementRef<HTMLImageElement>>;
  @ViewChildren('imageElement') imagesElements?: QueryList<ElementRef<HTMLImageElement>>;

  /**
   * Liste des items avec un statut pour savoir s'ils sont chargés
   * Clé -> item.id
   * Valeur -> vrai si l'item est chargé, sinon faux
   * @private
   */
  private itemsLoaded = new Map<number, boolean>();

  protected readonly FencePreviewItemTypeEnum = FencePreviewItemTypeEnum;

  constructor(private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    this.setItemsLoaded(this.item);
  }

  /**
   * Permet de définir qu'un item est chargé
   * @param item
   * @private
   */
  private setItemsLoaded(item: FencePreviewItem) {
    let itemsToLoad = [item];

    if (item.product.type === FencePreviewItemTypeEnum.Board) {
      itemsToLoad = item.product.items.filter(item => item.product.path);
    }

    this.itemsLoaded.clear();

    for (const itemToLoad of itemsToLoad) {
      this.itemsLoaded.set(itemToLoad.position, false);
    }

    this.checkImagesIsAlreadyLoaded();

    this.emitIsLoaded();
  }

  /**
   * Check si les images sont déjà chargées
   * Si c'est le cas, on change le status de l'item dans itemsLoaded
   * @private
   */
  private checkImagesIsAlreadyLoaded(): void {
    this.imagesElements?.forEach(img => {
      // on va chercher les items qui sont affichés dans la preview
      // dans le cas d'un panneau, on va chercher l'ensemble des sous-items le constituant
      let item =
        this.item.product.path === img.nativeElement.src
          ? this.item
          : this.item.product.items?.find(i => i.product.path === img.nativeElement.src);

      if (item && img.nativeElement.complete && img.nativeElement.naturalWidth > 0) {
        this.itemsLoaded.set(item.position, true);
      }
    });
  }

  private emitIsLoaded(): void {
    // recheck à chaque image chargée si une autre image n'a pas été mise en cache entre temps
    this.checkImagesIsAlreadyLoaded();

    if (this.itemsLoaded.size === 0 || Array.from(this.itemsLoaded.values()).some(isLoaded => !isLoaded)) {
      return;
    }

    this.isLoaded.emit(true);
  }

  setImageLoaded(item: FencePreviewItem): void {
    this.itemsLoaded.set(item.position, true);
    this.emitIsLoaded();
  }

  onBoardImageLoad(item: FencePreviewItem): void {
    this.setImageLoaded(item);
  }

  onBoardImageError(item: FencePreviewItem): void {
    this.setImageLoaded(item);
  }

  onImgError(item: FencePreviewItem): void {
    this.setImageLoaded(item);
  }

  onImgLoad(item: FencePreviewItem) {
    let percentageRecouvrement = 0;
    switch (item?.product?.type) {
      case FencePreviewItemTypeEnum.SealedPost:
      case FencePreviewItemTypeEnum.PostWithPlate:
        percentageRecouvrement = -28;
        break;
      case FencePreviewItemTypeEnum.Door:
        percentageRecouvrement = -1;
        break;
      case FencePreviewItemTypeEnum.VerticalDecoration:
      case FencePreviewItemTypeEnum.VerticalPanel:
        percentageRecouvrement = -1;
        break;
    }

    let element = document.getElementById('element' + this.item?.position);
    let width = element?.clientWidth;
    let margin = width * (percentageRecouvrement / 100);

    this.renderer.setStyle(element.parentElement, 'margin-left', margin + 'px');
    this.renderer.setStyle(element.parentElement, 'margin-right', margin + 'px');

    this.setImageLoaded(item);
  }

  /**
   * Calcule la hauteur de l'entretoise dans la preview
   * On fait un ratio de la hauteur réelle de l'entretoise * la hauteur d'une lame dans la preview / la hauteur réelle de la lame
   * @param spacerHeight
   * @protected
   */
  protected getSpacerHeightWithRatio(spacerHeight: number): number {
    if (!this.boardImages || this.boardImages.toArray().length === 0) {
      return 0;
    }

    // Récupération de la hauteur réelle de la lame
    const boardHeight =
      this.item.product.items.find(item => item.product.type === FencePreviewItemTypeEnum.Board)?.product?.height ?? 0;

    // Récupération de la hauteur de l'image de la lame
    const boardImageElementHeight = Math.min(...this.boardImages.toArray().map(i => i.nativeElement.clientHeight));

    if (!boardImageElementHeight) {
      return 0;
    }

    // calcul de la hauteur de la preview
    const height = Math.floor((spacerHeight * boardImageElementHeight) / boardHeight);

    // si jamais la hauteur passe à 0 avec on retourne au moins 1 pour afficher l'entretoise visuellement
    return height > 0 ? height : 1;
  }
}
